// Canonical payment status helpers.
// PaymentInfo.status is canonicalized to: unpaid | partial | paid | overpaid.
// Invoice.paymentStatus remains UI-friendly legacy values (pending | partial | paid | overdue | returned).

export type CanonicalPaymentInfoStatus = 'unpaid' | 'partial' | 'paid' | 'overpaid'
export type InvoicePaymentStatus = 'pending' | 'partial' | 'paid' | 'overdue' | 'returned'

const roundMoney = (value: unknown): number => {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return 0
  return Math.round(amount * 100) / 100
}

export const normalizePaymentInfoStatus = (value: unknown): CanonicalPaymentInfoStatus => {
  const status = String(value || '').trim().toLowerCase()
  if (status === 'paid') return 'paid'
  if (status === 'partial') return 'partial'
  if (status === 'overpaid') return 'overpaid'
  // Legacy values and unknown states are treated as unpaid at the PaymentInfo boundary.
  return 'unpaid'
}

export const toInvoicePaymentStatus = (status: CanonicalPaymentInfoStatus): InvoicePaymentStatus => {
  if (status === 'paid') return 'paid'
  if (status === 'overpaid') return 'paid'
  if (status === 'partial') return 'partial'
  return 'pending'
}

export const derivePaymentInfoStatusFromAmounts = (
  paidAmountRaw: unknown,
  totalAmountRaw: unknown,
  tolerance: number = 0.01
): CanonicalPaymentInfoStatus => {
  const paidAmount = roundMoney(paidAmountRaw)
  const totalAmount = roundMoney(totalAmountRaw)

  if (paidAmount > totalAmount + tolerance) return 'overpaid'
  if (paidAmount >= totalAmount - tolerance && totalAmount > 0) return 'paid'
  if (paidAmount > 0) return 'partial'
  return 'unpaid'
}

export const normalizeInvoicePaymentStatus = (value: unknown): InvoicePaymentStatus => {
  const status = String(value || '').trim().toLowerCase()
  if (status === 'paid') return 'paid'
  if (status === 'partial') return 'partial'
  if (status === 'overdue') return 'overdue'
  if (status === 'returned') return 'returned'
  // Map legacy unpaid to pending for existing UI filters/badges.
  return 'pending'
}

export const normalizeInvoicePaymentFields = <T extends Record<string, any>>(invoice: T): T => {
  if (!invoice || typeof invoice !== 'object') {
    return invoice
  }

  const payment = typeof invoice.payment === 'object' && invoice.payment ? invoice.payment : {}
  const normalizedPaymentStatus = normalizePaymentInfoStatus(payment.status)
  const normalizedTopLevelStatus = normalizeInvoicePaymentStatus(invoice.paymentStatus)

  return {
    ...invoice,
    payment: {
      ...payment,
      status: normalizedPaymentStatus
    },
    paymentStatus: normalizedTopLevelStatus
  }
}
